import { useTranslation } from "react-i18next";
import { Form, Button, InputNumber, Typography, Input } from "antd";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { postApiRequest } from "../../services/postApiRequest";
import { forgotPassword } from "../../services/Api";
import { showToast } from "../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import { END_POINTS } from "../../Helper/Constant";
import { getLocalStorageData, setLocalStorageData } from "../../shared/commonFunction";
import { setLoginState } from "../../features/login/loginSlice";
import { hideLoader, showLoader } from "../../features/loader/loaderSlice";
import "./VerifyOtp.scss";

const VerifyOtp = () => {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showOtpForm, setShowOtpForm] = useState(true);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle OTP verification
  const handleOtpVerification = useCallback(async (values) => {
    dispatch(showLoader());
    try {
      const response = await postApiRequest(END_POINTS.VERIFY_OTP, values);
      if (response?.success) {
        showToast(response.message, "success");
        setLocalStorageData("token", response.token);
        dispatch(setLoginState(true));
        setLocalStorageData("user", JSON.stringify(response?.user))
        form.resetFields();
        navigate("/");
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      showToast(error?.error?.message || t("forgot_password.errorOccurred"), "error");
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch, form, navigate, t]);

  // Handle email change
  const handleEmailChange = useCallback(async (values) => {
    dispatch(showLoader());
    try {
      const response = await postApiRequest(END_POINTS.CHANGE_EMAIL, values);
      if (response?.success) {
        setLocalStorageData("email", values.email);
        showToast(response.message, "success");
        form.resetFields();
        setShowOtpForm(true);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      console.error("Email change error:", error);
      showToast(error?.error?.message || t("forgot_password.errorOccurred"), "error");
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch, form, t]);

  // Handle OTP resend
  const resendOtp = useCallback(async () => {
    const email = getLocalStorageData("email");
    dispatch(showLoader());
    try {
      const response = await forgotPassword(email);
      if (response?.success) {
        form.resetFields();
        showToast(response.message, "success");
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      showToast(error?.message || t("forgot_password.errorOccurred"), "error");
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch, form, t]);

  // Email validation
  const validateEmail = useCallback((_, value) => {
    if (!value) {
      return Promise.reject(
        t("signUp.requiredField", { field: t("signUp.email") })
      );
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return Promise.reject(t("signUp.invalidEmail"));
    }
    return Promise.resolve();
  }, [t]);

  return (
    <section className="forgot-pass-section">
      <div className="forgot-main-box">
        <div>
          <h1 className="forgot-pass-heading">
            {showOtpForm ? t("forgot_password.otp_heading") : t("forgot_password.change_email")}
          </h1>
          <div className="forgot-up-box">
            {showOtpForm ? (
              <Form
                layout="vertical"
                form={form}
                onFinish={handleOtpVerification}
                style={{ maxWidth: 600 }}
                initialValues={{ prefix: "86" }}
              >
                <Form.Item
                  label={t("forgot_password.enter_otp")}
                  name="otp"
                  rules={[{
                    required: true,
                    message: t("forgot_password.otp_validation"),
                  }]}
                >
                  <InputNumber
                    placeholder={t("forgot_password.enter_otp")}
                    style={{ width: "100%" }}
                    dir="ltr"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    onClick={resendOtp}
                    className="submit-btn"
                    style={i18n.language === "ar" ? { marginRight: "10px" } : {}}
                  >
                    {t("forgot_password.resend")}
                  </Button>
                  <Button
                    type="primary"
                    style={{ marginLeft: "10px" }}
                    htmlType="submit"
                    className="submit-btn"
                  >
                    {t("forgot_password.button")}
                  </Button>
                </Form.Item>

                <Typography
                  className="use-different-email"
                  onClick={() => setShowOtpForm(false)}
                >
                  {t("forgot_password.use_different_email")}
                </Typography>
              </Form>
            ) : (
              <Form
                layout="vertical"
                form={form}
                onFinish={handleEmailChange}
                style={{ maxWidth: 600 }}
              >
                <Form.Item
                  label={t("forgot_password.enter_email_phone_username")}
                  name="oldCredential"
                  rules={[{
                    required: true,
                    message: t("forgot_password.email_change_validation")
                  }]}
                >
                  <Input
                    placeholder={t("forgot_password.email_change_validation")}
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label={t("signUp.new_email")}
                  rules={[{ validator: validateEmail }]}
                  className="common-field-input"
                >
                  <Input
                    placeholder={t("signUp.emailPlaceholder")}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-btn"
                    block
                  >
                    {t("signUp.change_email")}
                  </Button>
                </Form.Item>
              </Form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerifyOtp;